import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";
import { ANALYTICS_I18N } from "@/components/analytics/i18n";
import { StatCards, MastersTable, ChannelsBlock, ClientsBlock, ReactivationBlock, CancellationsBlock, ServicesBlock, Heatmap } from "@/components/analytics/AnalyticsBlocks";

const iso = (d) => d.toISOString().slice(0, 10);
const presetRange = (p) => {
  const today = new Date();
  if (p === "today") return [iso(today), iso(today)];
  if (p === "d7") return [iso(new Date(today - 6 * 86400000)), iso(today)];
  if (p === "month") return [iso(new Date(today.getFullYear(), today.getMonth(), 1)), iso(today)];
  return [iso(new Date(today - 29 * 86400000)), iso(today)];
};

export default function AnalyticsTab() {
  const { lang } = useTranslation();
  const L = ANALYTICS_I18N[lang] || ANALYTICS_I18N.en;
  const [preset, setPreset] = useState("d30");
  const [range, setRange] = useState(presetRange("d30"));
  const [branchId, setBranchId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true); setError("");
    axios.get(`${API}/analytics/overview`, { params: { date_from: range[0], date_to: range[1], branch_id: branchId || undefined, staff_id: staffId || undefined } })
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Error"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range, branchId, staffId]);

  const pick = (p) => { setPreset(p); if (p !== "custom") setRange(presetRange(p)); };
  const sel = "h-9 text-sm border-2 border-black rounded-md px-2 bg-white";
  const hasData = useMemo(() => data && (data.summary.bookings > 0 || data.summary.cancelled > 0), [data]);

  return (
    <div className="space-y-4 text-gray-900" data-testid="analytics-tab">
      <div className="bg-white border-2 border-black rounded-lg p-3 sm:p-4 flex flex-wrap items-center gap-2" data-testid="analytics-filters">
        <h2 className="text-lg md:text-xl font-bold mr-2">📊 {L.title}</h2>
        {["today", "d7", "d30", "month", "custom"].map((p) => (
          <button key={p} type="button" onClick={() => pick(p)} data-testid={`analytics-preset-${p}`}
            className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold ${preset === p ? "bg-black text-white border-black" : "border-gray-300 text-gray-600 hover:border-black"}`}>{L[p]}</button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-1 text-xs">
            <input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className={sel} data-testid="analytics-from" />
            <span>—</span>
            <input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className={sel} data-testid="analytics-to" />
          </div>
        )}
        <div className="ml-auto flex gap-2">
          {(data?.branches || []).length > 1 && (
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={sel} data-testid="analytics-branch">
              <option value="">{L.allBranches}</option>{data.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {(data?.staff || []).length > 0 && (
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} className={sel} data-testid="analytics-staff">
              <option value="">{L.allStaff}</option>{data.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="analytics-error">{error}</p>}
      {loading && !data && <p className="text-sm text-gray-500">{L.loading}</p>}
      {data && (
        <div className={`space-y-4 ${loading ? "opacity-60" : ""}`}>
          <StatCards s={data.summary} L={L} />
          {!hasData && <p className="text-sm text-gray-500 text-center py-6" data-testid="analytics-empty">{L.empty}</p>}
          <MastersTable rows={data.masters} L={L} />
          <ChannelsBlock rows={data.channels} L={L} />
          <ClientsBlock c={data.clients} L={L} />
          <div className="grid lg:grid-cols-2 gap-4">
            <ReactivationBlock r={data.reactivation} L={L} />
            <Heatmap cells={data.heatmap} L={L} />
          </div>
          <CancellationsBlock c={data.cancellations} L={L} />
          <ServicesBlock s={data.services} L={L} />
        </div>
      )}
    </div>
  );
}
